import React, { useRef } from "react";
import cn from "classnames";
import c from "../../styles/container.module.scss";
import s from "./RelatedContent.module.scss";
import bs from "../../styles/block.spacing.module.scss";
import BlockBackground from "../Common/BlockBackground";
import BlockIntro from "../Common/BlockIntro";
import {
  RelatedContentUIProps,
  StoryHit,
} from "../../lib/storyblok/types/blocks/RelatedContentBlock";
import StoryCard from "./components/StoryCard";
import type { SwiperRef } from "swiper/react";
import { Swiper, SwiperSlide } from "swiper/react";
import { useTranslation } from "next-i18next";
import useMediaQuery from "../../hooks/useMatchMedia";
import SliderButton from "../../ui/Common/SliderButton/SliderButton";
import Navigation from "./components/Navigation";
import { Masonry } from "masonic";

const RelatedContentUi: React.FC<RelatedContentUIProps> = ({
  title,
  description,
  buttons,
  innerSpacing,
  outerSpacing,
  inverted,
  style = "none",
  stories,
  contentLayout = "grid",
}) => {
  const { t } = useTranslation();
  const swiperRef = useRef<SwiperRef>(null);

  const isLaptop = useMediaQuery("(max-width: 1024px)");
  const isMobile = useMediaQuery("(max-width: 768px)");

  if (!stories || stories.length === 0) {
    return null;
  }

  const shouldShowNavigation =
    contentLayout === "slider" &&
    ((isMobile && stories.length > 1) ||
      (!isMobile && isLaptop && stories.length > 2) ||
      (!isLaptop && stories.length > 3));

  const slideNext = () => {
    if (swiperRef.current && swiperRef.current.swiper) {
      swiperRef.current.swiper.slideNext();
    }
  };

  const slidePrev = () => {
    if (swiperRef.current && swiperRef.current.swiper) {
      swiperRef.current.swiper.slidePrev();
    }
  };

  // Masonry
  const getItemHeight = (index: number) => {
    const heights = [280, 380, 320, 300, 200, 240, 380];
    return heights[index % heights.length];
  };

  const MasonryCard = ({ data, index }: { data: StoryHit; index: number }) => (
    <div className={s.masonryItem} style={{ height: getItemHeight(index) }}>
      <StoryCard
        story={data}
        showContent={false}
        showLabel={true}
        overlay={true}
      />
    </div>
  );

  const renderContent = () => {
    switch (contentLayout) {
      case "masonry":
        return (
          <div className={s.masonryContainer}>
            <Masonry
              key={`masonry-${stories.length}-${stories
                .map((s) => s.objectID)
                .join("-")}`}
              items={stories}
              columnGutter={32}
              columnWidth={isMobile ? 320 : 400}
              overscanBy={4}
              render={({ index, data }) => (
                <MasonryCard data={data} index={index} />
              )}
            />
          </div>
        );

      case "slider":
        return (
          <div className={s.sliderContainer}>
            <Swiper
              centeredSlides={false}
              loop={true}
              autoplay={{
                delay: 7000,
                disableOnInteraction: false,
                pauseOnMouseEnter: true,
              }}
              mousewheel={false}
              slidesPerView={1}
              spaceBetween={32}
              breakpoints={{
                768: { slidesPerView: 2 },
                1024: { slidesPerView: 3 },
              }}
              ref={swiperRef}
            >
              {stories.map((story) => (
                <SwiperSlide
                  key={`related-content-slider-${Math.random()
                    .toString(36)
                    .slice(2, 7)}`}
                >
                  <StoryCard
                    key={story.objectID}
                    story={story}
                    showContent={true}
                    showLabel={true}
                  />
                </SwiperSlide>
              ))}
            </Swiper>

            {shouldShowNavigation && (
              <Navigation>
                <SliderButton
                  onClick={slidePrev}
                  align="prev"
                  label={t("previous")}
                  inverted={inverted}
                />
                <SliderButton
                  onClick={slideNext}
                  align="next"
                  label={t("next")}
                  inverted={inverted}
                />
              </Navigation>
            )}
          </div>
        );

      case "grid":
        return (
          <ul className={s.gridLayout}>
            {stories.map((story) => (
              <li
                key={`grid-${Math.random().toString(36).slice(2, 7)}`}
                className={s.gridItem}
              >
                <StoryCard
                  key={story.objectID}
                  story={story}
                  showContent={true}
                  showLabel={true}
                />
              </li>
            ))}
          </ul>
        );

      case "gridCompact":
        return (
          <ul className={cn(s.gridLayout, s.gridCompact)}>
            {stories.map((story) => (
              <li
                key={`grid-compact-${Math.random().toString(36).slice(2, 7)}`}
                className={s.gridItem}
              >
                <StoryCard
                  key={story.objectID}
                  story={story}
                  showContent={false}
                  showLabel={false}
                />
              </li>
            ))}
          </ul>
        );

      case "gridSlim":
        return (
          <ul className={cn(s.gridLayout, s.gridSlim)}>
            {stories.map((story) => (
              <li
                key={`grid-slim-${Math.random().toString(36).slice(2, 7)}`}
                className={s.gridItem}
              >
                <StoryCard
                  key={story.objectID}
                  story={story}
                  showContent={false}
                  showLabel={false}
                  slim={true}
                />
              </li>
            ))}
          </ul>
        );

      default:
        return (
          <div className={s.gridLayout}>
            {stories.map((story) => (
              <div
                key={`related-content-default-${
                  story.objectID || Math.random().toString(36).slice(2, 7)
                }`}
                className={s.gridItem}
              >
                <StoryCard story={story} />
              </div>
            ))}
          </div>
        );
    }
  };

  return (
    <section
      className={cn(s.relatedContent, bs[innerSpacing], bs[outerSpacing])}
    >
      <BlockBackground style={style} opacity={100} />

      <BlockIntro
        inverted={inverted}
        title={title}
        description={description}
        buttons={buttons}
        alignment="alignCenter"
        width={"contained"}
      />

      <div className={cn(s.relatedContentInner, c.xWide)}>
        {renderContent()}
      </div>
    </section>
  );
};

export default RelatedContentUi;
